import React, { useEffect } from "react";
import {
    ApolloClient,
    ApolloProvider,
    createHttpLink,
    from,
    InMemoryCache,
    NormalizedCacheObject,
    split
} from "@apollo/client";
import {
    AnalyticsState,
    BuildsState,
    EditPanelState,
    MutatorState,
    NotificationState,
    PaginatorState,
    SortingState,
    State
} from "../models/common";
import { getMainDefinition } from "@apollo/client/utilities";
import { setContext } from "@apollo/client/link/context";
import { useAuth0 } from "@auth0/auth0-react";
import {
    getDefaultFunnelTimestamps,
    getDefaultTimestamps
} from "../common/AnalyticsHelpers";
import {
    AnalyticsLoadingState,
    ProjectEnvironment,
    SortingMode
} from "../models/types";
import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev";
import { getEnvironment } from "../common/Helpers";
import { createClient } from "graphql-ws";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";

const { REACT_APP_URL, REACT_APP_WS_API_URL } = process.env;

let apolloClientInstance: ApolloClient<NormalizedCacheObject> | null = null;
let refreshPromise: Promise<string | undefined> | null = null;
let tokenGetter: (() => Promise<string | undefined>) | null = null;
let persistentError: Error | null = null;
let redirectInProgress = false;
let isDestroyingApolloClient = false;

export const initialLocalState: State = {
    initialized: false,
    buildNotifications: [],
    isDeveloper: true,
    useDarkMode: false,
    showArchived: false,
    cuttlefishMode: false,
    redirect: undefined,
    productTier: new Set<number>([]),
    projectTier: new Set<number>([]),
    searchTerm: undefined,
    id: "1",
    __typename: "state"
};

export const initialLocalAnalyticsState: AnalyticsState = {
    analyticsEnabled: false,
    analyticsSelectedTab: "funnels",
    funnelApiVersion: "1.0.0",
    analyticsFunnelSelectionMode: false,
    analyticsProducts: [],
    analyticsProjects: [],
    analyticsRevisions: [],
    analyticsBuilds: [],
    analyticsNetworkBuilds: [],
    analyticsTask: undefined,
    analyticsLoadingState: AnalyticsLoadingState.Ready,
    allCtrAnalyticsResults: [],
    funnelAnalyticsResults: [],
    funnelEvents: [
        { eventName: "", eventData: [] },
        { eventName: "", eventData: [] }
    ],
    analyticsTimestamps: getDefaultTimestamps(),
    funnelTimestamps: getDefaultFunnelTimestamps(),
    includeBuildPreview: false,
    includeBuildComparison: false,
    includeDayFunnel: false,
    includeCtaBtnHeatmaps: false,
    includeTimeMedians: false,
    includeTimeDistribution: false,
    clearCache: true,
    id: "1",
    __typename: "analyticsState"
};

export const initialLocalBuildsState: BuildsState = {
    buildJobsData: undefined,
    id: "1",
    __typename: "buildState"
};

export const initialNotificationState: NotificationState = {
    showLoading: false,
    showToast: false,
    toastData: {
        success: true,
        header: "Build Status",
        message: "Success",
        delay: 3000
    },
    id: "1",
    __typename: "buildState"
};

export const initialPaginatorState: PaginatorState = {
    currentPage: 1,
    id: "1",
    __typename: "paginatorState"
};

export const initialEditPanelState: EditPanelState = {
    legacyLocalizationCheck: false,
    legacyLocalizationErrorString: "",
    pathTemplateCheck: false,
    mutatorConfigCheck: false,
    templatorConfigCheck: false,
    defaultLocalizationMissing: false,
    compressionCheck: false,
    jobDuplicateCheck: false,
    id: "1",
    __typename: "editPanelState"
};

export const initialSortingState: SortingState = {
    id: "1",
    sortingPriorities: [SortingMode.None, SortingMode.None, SortingMode.None],
    organizationSorting: false,
    favoritesSorting: false,
    __typename: "sortingState"
};

export const initialMutatorState: MutatorState = {
    id: "1",
    showMeta: false,
    __typename: "mutatorState"
};

const options = {
    typePolicies: {
        Query: {
            fields: {
                state: {
                    merge: true
                },
                projects: {
                    merge: false
                },
                revisions: {
                    merge: false
                },
                products: {
                    merge: false
                },
                emptyProjects: {
                    merge: false
                },
                pendingRevisions: {
                    merge: false
                },
                getUserAnalyticsQueries: {
                    merge: false
                },
                fileUploads: {
                    merge: false
                },
                getEventsByOrgId: {
                    merge: false
                },
                mutatorConfig: {
                    merge: false
                },
                mutatorAssetbundles: {
                    merge: false
                },
                getNotifications: {
                    merge: false
                },
                userProducts: {
                    merge: false
                },
                userProjects: {
                    merge: false
                }
            }
        }
    }
};

const createApolloClient = () => {
    if (apolloClientInstance) {
        return apolloClientInstance;
    }

    const httpLink = createHttpLink({
        uri: `${REACT_APP_URL}/api/`
    });

    const authLink = setContext(async () => {
        const token = tokenGetter ? await tokenGetter() : undefined;
        if (!token) {
            return {};
        }
        const headers = token
            ? {
                  Authorization: `Bearer ${token}`
              }
            : {};
        return {
            headers: headers
        };
    });

    const wsLink = new GraphQLWsLink(
        createClient({
            url: REACT_APP_WS_API_URL || "",
            connectionParams: async () => {
                const token = tokenGetter ? await tokenGetter() : undefined;
                const tokenString = `Bearer ${token}`;
                return {
                    token: tokenString
                };
            },
            retryAttempts: 5,
            lazy: true
        })
    );

    const splitLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return (
                definition.kind === "OperationDefinition" &&
                definition.operation === "subscription"
            );
        },
        wsLink,
        from([authLink, httpLink])
    );

    apolloClientInstance = new ApolloClient({
        link: splitLink,
        cache: new InMemoryCache(options)
    });

    if (getEnvironment() === ProjectEnvironment.Local) {
        // Adds messages only in a dev environment
        loadDevMessages();
        loadErrorMessages();
    }

    return apolloClientInstance;
};

export const destroyApolloClient = () => {
    if (apolloClientInstance) {
        apolloClientInstance.stop();
        apolloClientInstance.clearStore();
        apolloClientInstance = null;
    }
    tokenGetter = null;
    refreshPromise = null;
    persistentError = null;
};

// @ts-ignore
export const AuthorizedApolloProvider = ({ children }) => {
    const {
        isLoading,
        isAuthenticated,
        getAccessTokenSilently,
        loginWithRedirect,
        logout
    } = useAuth0();

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            console.log("[DEBUG] resetting errors ");
            persistentError = null;
            redirectInProgress = false;
            isDestroyingApolloClient = false;
            refreshPromise = null;
        }

        if (!isLoading && !isAuthenticated) {
            const returnPath = `${window.location.pathname}${window.location.search}`;
            const email = new URLSearchParams(location.search).get("email");
            loginWithRedirect({
                appState: {
                    returnTo: returnPath
                },
                authorizationParams: {
                    login_hint: email || undefined,
                    screen_hint: email ? "signup" : "login",
                    prompt: "select_account"
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, isAuthenticated]);

    const getAccessTokenSafely = async (): Promise<string | undefined> => {
        if (redirectInProgress || isDestroyingApolloClient) {
            return undefined;
        }

        if (persistentError) {
            throw persistentError;
        }

        if (!refreshPromise) {
            refreshPromise = (async () => {
                try {
                    const token = await getAccessTokenSilently();
                    return token;
                } catch (err: any) {
                    console.log("[DEBUG] error ", err);
                    console.log("[DEBUG] err?.error ", err?.error);
                    console.log("[DEBUG] err?.message ", err?.message);
                    const authError = err?.error || err?.message;
                    if (
                        authError === "invalid_grant" ||
                        authError === "missing_refresh_token"
                    ) {
                        if (!redirectInProgress) {
                            redirectInProgress = true;
                            isDestroyingApolloClient = true;
                            localStorage.clear();
                            destroyApolloClient();
                            await logout({
                                logoutParams: {
                                    returnTo: `${window.location.origin}`
                                }
                            });
                        }
                        return undefined;
                    } else if (
                        authError === "login_required" ||
                        authError === "consent_required"
                    ) {
                        if (!redirectInProgress) {
                            redirectInProgress = true;
                            await loginWithRedirect({
                                appState: {
                                    returnTo:
                                        window.location.pathname +
                                        window.location.search
                                }
                            });
                        }
                        return undefined;
                    }

                    console.error(
                        "[Auth0] Unexpected token retrieval error:",
                        err
                    );
                    persistentError = err;
                    throw err;
                } finally {
                    refreshPromise = null;
                }
            })();
        }

        return refreshPromise;
    };

    tokenGetter = getAccessTokenSafely;
    const apolloClient = createApolloClient();

    return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};
